import React from 'react';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const SurfaceCleaning = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: "Driveway Cleaning",
      description: "Concrete, asphalt, paver, and stone driveways restored to like-new condition."
    },
    {
      title: "Walkway & Sidewalk Cleaning",
      description: "Safe, even cleaning for brick, pavers, and concrete paths."
    },
    {
      title: "Patio & Terrace Cleaning",
      description: "Bluestone, travertine, flagstone, limestone, brick, and concrete patios refreshed and protected."
    },
    {
      title: "Paver & Stone Cleaning",
      description: "Revives color and removes algae from natural stone (bluestone, slate, limestone, flagstone) and man-made pavers."
    },
    {
      title: "Deck Cleaning (Wood & Composite)",
      description: "We carefully clean cedar, mahogany, ipe, Brazilian hardwoods, pressure-treated lumber, and composite materials (Trex, TimberTech, PVC, vinyl). Because these surfaces can be damaged by high pressure, we use a gentle soft washing method that removes algae and mildew while protecting the finish."
    },
    {
      title: "Commercial Surface Cleaning",
      description: "Entrances, sidewalks, and outdoor areas for restaurants, clubs, hotels, and retail properties."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <ServiceHero
        title="Surface Cleaning"
        headline="Stained Surfaces?"
        description="Professional surface cleaning for driveways, patios, decks, and walkways. We restore your hardscapes while protecting your investment."
        backgroundImage="/images/services/soft-washing.webp"
        defaultService="Surface Cleaning (walkway, patio, etc)"
      />
      
      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6">
                Our Surface Cleaning Services
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Outdoor surfaces in the Hamptons take a beating from <strong>salt air, sandy winds, 
                humidity, and heavy use</strong>. Over time, driveways, patios, decks, and walkways 
                collect <strong>dirt, algae, mildew, rust, and stains</strong> that make them dull, discolored, and 
                even slippery.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                At <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>, we provide <strong>professional surface cleaning</strong> that restores your 
                hardscapes while protecting your investment. Using commercial-grade flat-surface 
                cleaning equipment and eco-friendly solutions, we deliver a <strong>deep, streak-free 
                clean</strong> that pressure washing alone can't achieve.
              </p>

              <p className="text-gray-700 font-medium mb-4">
                Our surface cleaning services include:
              </p>

              <ul className="space-y-4 mb-8">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-900 font-semibold">{service.title} – </span>
                      <span className="text-gray-700">{service.description}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mb-8">
                <p className="font-semibold text-gray-900 mb-2">We Also Offer:</p>
                <p className="text-gray-700">
                  Beyond Surface Cleaning, our team is trusted across Long Island for{' '}
                  <strong>Window Cleaning, House Washing, Roof Washing, Gutter Cleaning, 
                  and Shrink Wrapping</strong>.{' '}
                  <Link to="/#services" className="text-blue-600 hover:text-blue-700 font-medium">
                    Explore all our services <ArrowRight className="inline w-4 h-4" />
                  </Link>
                </p>
              </div>

              <button
                onClick={scrollToQuote}
                data-testid="surface-cleaning-cta"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Request a Quote
              </button>
              <p className="mt-3 text-sm text-gray-600">
                Get within 1 business day
              </p>
            </div>

            {/* Right Images */}
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                {/* Main Image - Deck Before/After */}
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_63cc0cbd-291e-4bc6-b7a0-dfd1144cdf11/artifacts/dvjt0ytj_deck%20wash%20new%202jpg.jpg"
                    alt="Deck cleaning before and after"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Top Right */}
                  <div className="absolute -top-4 -right-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8" />
                      <div>
                        <div className="text-2xl font-bold">15+ Years</div>
                        <div className="text-sm text-blue-100">Of Experience</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Image - Soft Washing Equipment */}
                <div className="relative">
                  <img
                    src="/images/services/soft-washing.webp"
                    alt="Professional surface cleaning equipment"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-xl">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">1450+ Clients</div>
                        <div className="text-sm text-gray-600">Satisfied By Us</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Restore Your Outdoor Surfaces?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation quote within 1 business day. 
            Our surface cleaning experts are ready to make your property look brand new.
          </p>
          <Link
            to="/#quote"
            data-testid="surface-cleaning-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default SurfaceCleaning;
